import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import UserLayout from '../components/UserLayout';
import Card, { CardBody } from '../components/Card';
import Button from '../components/Button';
import { SearchIcon, ArrowLeftIcon, FileTextIcon, ClockIcon, CreditCardIcon, GridIcon, ListIcon, FilterIcon, BuildingIcon, HashIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { services } from '../data/services';
import { mdas } from '../data/mdas';
// Define view types for service display
type ViewType = 'mda' | 'alphabetical' | 'code';
const ServicesPage: React.FC = () => {
  const {
    mdaId
  } = useParams<{
    mdaId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialViewType = searchParams.get('view') as ViewType || 'mda';
  const [searchTerm, setSearchTerm] = useState('');
  const [mda, setMda] = useState(mdas.find(m => m.id === mdaId));
  const [mdaServices, setMdaServices] = useState(services.filter(s => s.mdaId === mdaId));
  const [viewType, setViewType] = useState<ViewType>(initialViewType);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filteredServices, setFilteredServices] = useState(mdaServices);
  const [allServices, setAllServices] = useState(services);
  // Update MDA and services when mdaId changes
  useEffect(() => {
    if (mdaId) {
      setMda(mdas.find(m => m.id === mdaId));
      setMdaServices(services.filter(s => s.mdaId === mdaId));
      setFilteredServices(services.filter(s => s.mdaId === mdaId));
    } else {
      setMdaServices(services);
      setFilteredServices(services);
    }
  }, [mdaId]);
  // Filter services based on search term
  useEffect(() => {
    const servicesToFilter = mdaId ? mdaServices : services;
    if (!searchTerm) {
      setFilteredServices(servicesToFilter);
      return;
    }
    const filtered = servicesToFilter.filter(service => {
      const searchLower = searchTerm.toLowerCase();
      // Search by name
      if (service.name.toLowerCase().includes(searchLower)) return true;
      // Search by description (keywords)
      if (service.description.toLowerCase().includes(searchLower)) return true;
      // Search by MDA
      const serviceMda = mdas.find(m => m.id === service.mdaId);
      if (serviceMda && serviceMda.name.toLowerCase().includes(searchLower)) return true;
      // Search by service ID (code)
      if (service.id.toLowerCase().includes(searchLower)) return true;
      return false;
    });
    setFilteredServices(filtered);
  }, [searchTerm, mdaServices, mdaId, services]);
  // Update view type in URL when it changes
  useEffect(() => {
    setSearchParams({
      view: viewType
    });
  }, [viewType, setSearchParams]);
  // Handle search input
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  // Change view type
  const changeViewType = (type: ViewType) => {
    setViewType(type);
    setShowFilterDropdown(false);
  };
  // Get sorted services based on view type
  const getSortedServices = () => {
    if (viewType === 'alphabetical') {
      return [...filteredServices].sort((a, b) => a.name.localeCompare(b.name));
    } else if (viewType === 'code') {
      return [...filteredServices].sort((a, b) => a.id.localeCompare(b.id));
    }
    return filteredServices;
  };
  // Group services by MDA
  const getServicesByMda = () => {
    const grouped: Record<string, typeof services> = {};
    filteredServices.forEach(service => {
      if (!grouped[service.mdaId]) {
        grouped[service.mdaId] = [];
      }
      grouped[service.mdaId].push(service);
    });
    return grouped;
  };
  // Render services in MDA view
  const renderMdaView = () => {
    const groupedServices = getServicesByMda();
    return Object.entries(groupedServices).map(([mdaId, mdaServices]) => {
      const currentMda = mdas.find(m => m.id === mdaId);
      return <div key={mdaId} className="mb-8" data-id="element-6356">
          <div className="flex items-center mb-4" data-id="element-6357">
            {currentMda?.logoUrl && <img src={currentMda.logoUrl} alt={`${currentMda.name} Logo`} className="h-8 w-8 mr-2" data-id="element-6358" />}
            <h2 className="text-xl font-bold text-gray-900" data-id="element-6359">
              {currentMda?.name || 'Unknown MDA'}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6360">
            {mdaServices.map(service => renderServiceCard(service))}
          </div>
        </div>;
    });
  };
  // Render services in alphabetical or code view
  const renderListView = () => {
    const sortedServices = getSortedServices();
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6361">
        {sortedServices.map(service => renderServiceCard(service))}
      </div>;
  };
  // Render individual service card
  const renderServiceCard = (service: (typeof services)[0]) => {
    const serviceMda = mdas.find(m => m.id === service.mdaId);
    return <Link key={service.id} to={`/services/${service.id}`} data-id="element-6362">
        <Card className="h-full hover:shadow-md transition-shadow" data-id="element-6363">
          <CardBody className="p-6" data-id="element-6364">
            <div className="flex justify-between items-start mb-2" data-id="element-6365">
              <h2 className="text-xl font-semibold text-gray-900" data-id="element-6366">
                {service.name}
              </h2>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded" data-id="element-6367">
                {service.id}
              </span>
            </div>
            {viewType !== 'mda' && serviceMda && <div className="flex items-center mb-2" data-id="element-6368">
                <BuildingIcon className="h-3 w-3 text-gray-500 mr-1" data-id="element-6369" />
                <span className="text-xs text-gray-600" data-id="element-6370">{serviceMda.name}</span>
              </div>}
            <p className="text-gray-600 text-sm mb-4" data-id="element-6371">{service.description}</p>
            <div className="flex flex-wrap gap-2 mb-4" data-id="element-6372">
              <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-6373">
                <CreditCardIcon className="h-3 w-3 mr-1" data-id="element-6374" />₦
                {service.fee.toLocaleString()}
              </div>
              <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-6375">
                <ClockIcon className="h-3 w-3 mr-1" data-id="element-6376" />
                {service.processingTime}
              </div>
            </div>
            <div className="mt-auto pt-2 flex justify-between items-center" data-id="element-6377">
              <span className="text-xs text-gray-500" data-id="element-6378">
                {service.requirements.length} document(s) required
              </span>
              <span className="text-green-600 text-sm font-medium flex items-center" data-id="element-6379">
                Apply Now
                <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-6380" />
              </span>
            </div>
          </CardBody>
        </Card>
      </Link>;
  };
  return <UserLayout data-id="element-6381">
      <div className="py-8 px-4 sm:px-6 lg:px-8" data-id="element-6382">
        <div className="max-w-7xl mx-auto" data-id="element-6383">
          {/* Page Header */}
          {!mdaId && !mda ? <div className="mb-8" data-id="element-6384">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-6385">
                All Government Services
              </h1>
              <p className="text-gray-600 mt-1" data-id="element-6386">
                Browse and apply for services provided by various Ministries,
                Departments, and Agencies
              </p>
            </div> : <div className="mb-8" data-id="element-6387">
              <Link to="/mdas" className="inline-flex items-center text-green-600 hover:text-green-700 mb-4" data-id="element-6388">
                <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-6389" />
                Back to All MDAs
              </Link>
              <div className="flex items-center" data-id="element-6390">
                <img src={mda?.logoUrl} alt={`${mda?.name} Logo`} className="h-12 w-12 mr-4" data-id="element-6391" />
                <div data-id="element-6392">
                  <h1 className="text-2xl font-bold text-gray-900" data-id="element-6393">
                    {mda?.name}
                  </h1>
                  <p className="text-gray-600 mt-1" data-id="element-6394">{mda?.description}</p>
                </div>
              </div>
            </div>}
          {/* Search and Filter Bar */}
          <div className="mb-8 bg-white rounded-lg shadow p-4" data-id="element-6395">
            <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-6396">
              <div className="flex-grow relative" data-id="element-6397">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6398">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-6399" />
                </div>
                <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder={mdaId ? 'Search for a service by name, keyword, or service code' : 'Search by service name, keyword, MDA, or service code'} value={searchTerm} onChange={handleSearch} data-id="element-6400" />
              </div>
              <div className="relative" data-id="element-6401">
                <button onClick={() => setShowFilterDropdown(!showFilterDropdown)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-6402">
                  <FilterIcon className="h-4 w-4 mr-2" data-id="element-6403" />
                  View Options
                  <ChevronDownIcon className="h-4 w-4 ml-2" data-id="element-6404" />
                </button>
                {showFilterDropdown && <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-6405">
                    <div className="py-1" role="menu" aria-orientation="vertical" data-id="element-6406">
                      {!mdaId && <button className={`flex items-center px-4 py-2 text-sm w-full text-left ${viewType === 'mda' ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => changeViewType('mda')} data-id="element-6407">
                          <BuildingIcon className="h-4 w-4 mr-2" data-id="element-6408" />
                          Group by MDA
                          {viewType === 'mda' && <CheckIcon className="h-4 w-4 ml-auto" data-id="element-6409" />}
                        </button>}
                      <button className={`flex items-center px-4 py-2 text-sm w-full text-left ${viewType === 'alphabetical' ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => changeViewType('alphabetical')} data-id="element-6410">
                        <div className="h-4 w-4 mr-2" data-id="element-6411" />
                        Alphabetical Order
                        {viewType === 'alphabetical' && <CheckIcon className="h-4 w-4 ml-auto" data-id="element-6412" />}
                      </button>
                      <button className={`flex items-center px-4 py-2 text-sm w-full text-left ${viewType === 'code' ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => changeViewType('code')} data-id="element-6413">
                        <HashIcon className="h-4 w-4 mr-2" data-id="element-6414" />
                        By Service Code
                        {viewType === 'code' && <CheckIcon className="h-4 w-4 ml-auto" data-id="element-6415" />}
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
          {/* View Type Indicator */}
          <div className="mb-6 flex items-center" data-id="element-6416">
            <span className="text-sm text-gray-500 mr-2" data-id="element-6417">Viewing by:</span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-6418">
              {viewType === 'mda' && <BuildingIcon className="h-3 w-3 mr-1" data-id="element-6419" />}
              {viewType === 'alphabetical' && <div className="h-3 w-3 mr-1" data-id="element-6420" />}
              {viewType === 'code' && <HashIcon className="h-3 w-3 mr-1" data-id="element-6421" />}
              {viewType === 'mda' ? 'Ministry, Department, or Agency' : viewType === 'alphabetical' ? 'Alphabetical Order' : 'Service Code'}
            </span>
            {filteredServices.length > 0 && <span className="ml-2 text-sm text-gray-500" data-id="element-6422">
                ({filteredServices.length} service
                {filteredServices.length !== 1 ? 's' : ''})
              </span>}
          </div>
          {/* Services List */}
          {filteredServices.length > 0 ? viewType === 'mda' && !mdaId ? renderMdaView() : renderListView() : <div className="text-center py-12 bg-white rounded-lg shadow" data-id="element-6423">
              <SearchIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-6424" />
              <h3 className="mt-2 text-lg font-medium text-gray-900" data-id="element-6425">
                No services found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-6426">
                Try adjusting your search or filter to find what you're looking
                for.
              </p>
              <div className="mt-6" data-id="element-6427">
                <button onClick={() => setSearchTerm('')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-6428">
                  Clear Search
                </button>
              </div>
            </div>}
        </div>
      </div>
    </UserLayout>;
};
export default ServicesPage;